/**
 * @cookieString full cookie strings coming from the header
 * @cookieName name of the item you're looking for
 * @cookieDefault (optional) default value to be set for that cookie
 */
export function extractCookie(cookieString, cookieName, cookieDefault = null) {
  if (!cookieString) return null;

  try {
    return cookieString
      .split(';')
      .filter((str) => str.includes(cookieName))
      .reduce((_, str) => str.trim().split('=')[1], cookieDefault);
  } catch (e) {
    console.error('Defaulting to Null - ', e.message);
    return null;
  }
}

export function removePrecedingSlash(url) {
  if (url && url.startsWith('/')) {
    return url.slice(1);
  }
  return url;
}

const TRACKING_PARAM = /^(utm_[a-z_]+|fbclid|gclid|msclkid|dclid|twclid|igshid|mc_cid|mc_eid|_hsenc|_hsmi|trk)$/;

/**
 * @typedef {object} SplitTarget
 * @property {string} path - The portion before the first `?`, or the whole value when there is no query.
 * @property {URLSearchParams} params - The parsed query params, empty when there is no query.
 */

/**
 * Splits a redirect target or request URL into its path and query params.
 * A trailing bare `?` is kept as part of the path, so legacy stored rows such as `forms?` keep
 * matching only `/forms?` exactly as they do today.
 * @param {string} [value] - The request URL or stored redirect path. Nullish values are treated as ''.
 * @returns {SplitTarget} The literal path and its parsed query params.
 */
export function splitTarget(value) {
  const raw = value || '';
  const i = raw.indexOf('?');
  if (i === -1 || i === raw.length - 1) return { path: raw, params: new URLSearchParams() };
  return { path: raw.slice(0, i), params: new URLSearchParams(raw.slice(i + 1)) };
}

/**
 * Scores a stored redirect path against an incoming request so the most specific row can win.
 * A row matches when the base paths are equal, ignoring a leading slash on the stored path; when
 * the request carries exactly the same values for every stored key, regardless of order and
 * including repeats, so a surplus value on a stored key does not match; and when every extra
 * request param is a known tracking param (`utm_*`, `fbclid`, `gclid` and friends, which Facebook
 * and Google append to shared links). A non-tracking extra param blocks the match, so real routes
 * such as `/news?page=2` are not captured by a bare `news` row. Both arguments are expected to be
 * lowercased by the caller.
 * @param {string} storedPath - The redirect row path, with or without a leading slash or query string.
 * @param {SplitTarget} request - The incoming request, already split by {@link splitTarget}.
 * @returns {number} `-1` when the row does not match, otherwise the number of stored params matched,
 * used as a specificity score.
 */
export function matchScore(storedPath, request) {
  const stored = splitTarget(storedPath);
  if (stored.path !== request.path && `/${stored.path}` !== request.path) return -1;

  for (const key of new Set(stored.params.keys())) {
    const storedValues = stored.params.getAll(key).sort();
    const requestValues = request.params.getAll(key).sort();
    if (storedValues.length !== requestValues.length) return -1;
    if (storedValues.some((value, i) => value !== requestValues[i])) return -1;
  }
  for (const [key] of request.params) {
    if (!stored.params.has(key) && !TRACKING_PARAM.test(key)) return -1;
  }
  return [...stored.params].length;
}
